/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/DrawingsTab.tsx
 * PURPOSE: Drawings tab showing drawing references linked to the quote
 */

import { useQuery } from '@tanstack/react-query'
import { Image, ExternalLink, Loader2, AlertCircle } from 'lucide-react'
import { quoteApi } from '@/lib/quoteApi'

interface DrawingsTabProps {
  quoteId: string
}

export default function DrawingsTab({ quoteId }: DrawingsTabProps) {
  const { data: contextData, isLoading, error } = useQuery({
    queryKey: ['quoteContext', quoteId],
    queryFn: () => quoteApi.getQuoteContext(quoteId),
    staleTime: 30 * 1000,
  })
  const drawings = contextData?.drawings || []

  return (
    <div className="p-3 space-y-3">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Drawing References</h3>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" /> Failed to load drawings
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </div>
      ) : drawings.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <Image className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No drawing refs</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Add drawing refs to line items</p>
        </div>
      ) : (
        <div className="space-y-2">
          {drawings.map((drawing: any) => (
            <div key={drawing.id} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded">
                <Image className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">
                  {drawing.drawing_number}
                  {drawing.revision && <span className="text-slate-500"> Rev {drawing.revision}</span>}
                </p>
                {drawing.description && <p className="text-xs text-slate-500 truncate">{drawing.description}</p>}
                {drawing.section_title && <p className="text-xs text-slate-400 truncate">Section: {drawing.section_title}</p>}
              </div>
              <button className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded" title="View">
                <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
